import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { Button, Card, Cluster, Spinner, Stack } from "@salesapp/ui";
import { ApiError } from "../lib/api";
import { loxleyApi, type OAuthDetails } from "../lib/loxleyApi";
import { useAuth } from "../shell/AuthProvider";
import { LoxleyCharacter } from "./LoxleyCharacter";
import "./loxley.css";

/** The API packs the OAuth request into one base64url `r` parameter (login's returnTo refuses "://"). */
function unpack(packed: string | null): Record<string, string> | null {
  if (!packed) return null;
  try {
    const padded = packed.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((packed.length + 3) % 4);
    const parsed = JSON.parse(atob(padded)) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(([, v]) => typeof v === "string"),
    ) as Record<string, string>;
  } catch {
    return null;
  }
}

/**
 * "Allow Claude to use Saleslox as you?" Reached from /api/v1/oauth/authorize after
 * the normal identity-first login. Every value is re-validated by the API on approve.
 */
export function OAuthConsentPage() {
  const [searchParams] = useSearchParams();
  const { session } = useAuth();
  const params = useMemo(() => unpack(searchParams.get("r")), [searchParams]);
  const [details, setDetails] = useState<OAuthDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!params) {
      setError("This sign-in link is incomplete. Start connecting again from your AI app.");
      return;
    }
    loxleyApi
      .oauthDetails(params)
      .then(setDetails)
      .catch((err) => setError(err instanceof ApiError ? err.message : "This request could not be checked."));
  }, [params]);

  async function decide(approve: boolean) {
    if (!params || !details) return;
    setBusy(true);
    try {
      const { redirect } = await loxleyApi.oauthApprove(params, approve, details.scope);
      window.location.assign(redirect);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That did not work. Please try again.");
      setBusy(false);
    }
  }

  return (
    <div className="loxley-consent">
      <Card>
        <Stack gap={4}>
          <Cluster gap={3} align="center">
            <LoxleyCharacter className="loxley-consent-avatar" />
            <h1>Connect an AI app</h1>
          </Cluster>
          {error ? <p role="alert">{error}</p> : null}
          {!details && !error ? <Spinner /> : null}
          {details ? (
            <>
              <p>
                <strong>{details.client_name}</strong> wants to use Saleslox as{" "}
                <strong>{session?.email}</strong>
                {session?.orgName ? <> in <strong>{session.orgName}</strong></> : null}.
              </p>
              <ul>
                <li>It sees only what you can see, with your permissions.</li>
                <li>
                  {details.scope === "read"
                    ? "It can only read."
                    : "It can propose changes. Each one waits for you to confirm."}
                </li>
                <li>Deletes, money and anything sent to customers can only be approved inside Saleslox.</li>
                <li>You can disconnect it any time in User settings.</li>
              </ul>
              <p className="home-subtitle">After you choose, you go back to {details.redirect_host}.</p>
              {details.allowed ? (
                <Cluster gap={3}>
                  <Button onClick={() => void decide(true)} disabled={busy}>Allow</Button>
                  <Button variant="secondary" onClick={() => void decide(false)} disabled={busy}>Deny</Button>
                </Cluster>
              ) : (
                <p role="alert">{details.reason}</p>
              )}
            </>
          ) : null}
        </Stack>
      </Card>
    </div>
  );
}
